import { Box, Button, Divider, Pagination, Stack, Typography } from '@mui/material';
import plantumlEncoder from 'plantuml-encoder';
import React, { ChangeEvent, FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	deleteTicketUsecases,
	saveUseCases,
	updateUseCases,
} from 'store/ticket-service/asyncActions';
import {
	getActiveStep,
	getActiveTicket,
	getEditMode,
	getIsTicketNavigationButtonsAvailable,
	getIsUseCasesGenerating,
	getIsUsecasesLoading,
	getVoiceMode,
} from 'store/ticket-service/selector';
import {
	setActiveStep,
	setIsTicketUpdate,
	setIsTyping,
	setIsUsecasesGenerating,
	setIsUsecasesLoading,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import {
	IDiagram,
	IParsedTableData,
	IRawDiagram,
	ISummaryStory,
	IUsecaseData,
	IUsecases,
} from 'store/ticket-service/types';
import { fetchUserBalance } from 'store/user-service/asyncActions';
import { getChoosedFile } from 'store/user-service/selector';
import { triggerTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';
import { getNumericEnumValuesArray } from 'utils/getNumericEnumLength';
import { orderDiagrams } from 'utils/orderDiagrams';

import {
	getDiagramChangeRequest,
	getDiagramCorrection,
	getDiagramValidation,
	getFlowDiagram,
	getFlowOfEvents,
	getKeyRoles,
	getPreconditions,
	getSequenceDiagram,
	getSuccessfulOutcome,
	getTrigger,
	getUseCaseDiagram,
	getUsecaseConversation,
	getUserStoriesEpics,
	handleNotEnoughTokensError,
} from 'core/chains/ChatGptRequests';
import { IUseCase } from 'core/chains/types';
import { notEnoughTokensErrorMessage } from 'core/constants/chatGptRequestRegenerateMessages';
import {
	DiagramTypes,
	GeneralStepConversationTopics,
	MessageActionStates,
	MessageActionTypes,
	MessageSender,
	NamespacesList,
	SharePermissions,
	TicketStages,
	UseCaseTableFields,
} from 'core/enums/common';
import { getUmlUrl } from 'core/helpers/get-uml-url';
import { errorNotify } from 'core/helpers/toasts';
import useSpeechToText from 'core/hooks/useSpeechToText';
import useTextToSpeech from 'core/hooks/useTextToSpeech';

import { FormTitleValue } from 'pages/Home';

import { IMessage } from 'components/Chatbot/Message/types';
import { EditUsecasesStep } from 'components/TicketSteps/UsecasesStep/EditUsecaseStep';

import { ReactComponent as RegenerateIcon } from 'assets/icons/regenerate.svg';
import { ReactComponent as Stars } from 'assets/icons/stars.svg';
import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import UserStoriesList from '../GeneralStep/UserStoriesList';

import { DiagramImage } from './DiagramImage/DiagramImage';
import { FullViewDiagramModal } from './FullViewDiagramModal';
import { generateUseCaseDescriptionJSX } from './UseCaseTableBuilder';
import { UserStories } from './styled';

interface IRequestDetails {
	type: 'Use Case Diagram' | 'Sequence Diagram' | 'Flowchart';
	request: string;
}

interface UsecasesStepProps {
	messages: IMessage[];
	setMessages: (messages: Array<IMessage>) => void;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

interface ITableDataProps {
	userStories: string;
	useCase: IUseCase;
}

const linksPerPage = 1;
const maxConcurrentUseCaseRequests = 2;

export const UsecasesStep: FC<UsecasesStepProps> = ({
	messages,
	setMessages,
	ticketTitle,
	setTitleError,
}) => {
	const { t: useCasesTranslate, i18n } = useTranslation(NamespacesList['useCasesStep']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const translationConfig = useMemo(
		() => ({
			translate: useCasesTranslate,
			instance: i18n,
		}),
		[useCasesTranslate, i18n]
	);

	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener } = useEventBus();
	const { playText, hasFinishedSpeaking } = useTextToSpeech();
	const { startRecognition } = useSpeechToText();

	const activeTicket = useSelector(getActiveTicket);
	const activeStep = useSelector(getActiveStep);
	const isRequestLoading = useSelector(getIsUsecasesLoading);
	const voiceMode = useSelector(getVoiceMode);
	const editMode = useSelector(getEditMode);
	const choosedFile = useSelector(getChoosedFile);
	const isUseCasesGenerating = useSelector(getIsUseCasesGenerating);
	const isNavigationButtonAvailable = useSelector(getIsTicketNavigationButtonsAvailable);

	const [pageData, setPageData] = useState<Array<IUsecaseData>>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isFullViewDiagramModalOpen, setIsFullViewDiagramModalOpen] = useState(false);
	const [modalDiagramObj, setModalDiagramObj] = useState<IDiagram>();
	const [areDiagramsLoading, setAreDiagramsLoading] = useState<boolean>(false);

	const usecaseRef: RefObject<HTMLAnchorElement> = useRef(null);

	const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);

		if (usecaseRef.current) {
			usecaseRef.current.scrollTo({ top: 0 });
		}
	};

	useEffect(() => {
		if (hasFinishedSpeaking) startRecognition();
	}, [hasFinishedSpeaking]);

	useEffect(() => {
		if (activeTicket?.useCases?.length) {
			setPageData(activeTicket.useCases);
		}
	}, [activeTicket?.useCases]);

	const currentPageData = useMemo(() => {
		const indexOfLastLink = currentPage * linksPerPage;
		const indexOfFirstLink = indexOfLastLink - linksPerPage;
		return pageData.slice(indexOfFirstLink, indexOfLastLink);
	}, [currentPage, pageData]);

	const getIndexedFunctionalUserStories: string = useMemo(
		() =>
			activeTicket?.ticketSummaries?.length
				? activeTicket.ticketSummaries
						.filter(
							(story) => story.subStage === GeneralStepConversationTopics['Functional requirements']
						)
						.reduce(
							(accumulator, story, index) =>
								accumulator +
								`${index}.${story.data} ` +
								(story?.summaryScenarios?.length
									? `${commonTranslate('modelsInput.scenarios')}: ` +
									  story.summaryScenarios.reduce(
											(scenariosAccumulator, scenario, scenarioIndex) =>
												scenariosAccumulator + `${index}.${scenarioIndex}.${scenario.description} `,
											''
									  )
									: ''),
							''
						)
				: '',
		[activeTicket?.ticketSummaries, activeTicket?.id]
	);

	const getRelatedUserStories = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story) =>
						accumulator + `${commonTranslate('modelsInput.story')}: ${story.data}.`,
					''
			  )
			: '';

	const getRelatedUserStoriesWithScenarios = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story) =>
						accumulator +
						`${commonTranslate('modelsInput.story')}: ${story.data}.` +
						(story?.summaryScenarios?.length
							? `${commonTranslate('modelsInput.scenarios')}: ` +
							  story.summaryScenarios.reduce(
									(scenariosAccumulator, scenario, index) =>
										scenariosAccumulator + `${index + 1}.${scenario.description}`,
									''
							  )
							: ''),
					''
			  )
			: '';

	const getRelatedUserStoriesWithAcceptanceCriteria = (stories: Array<ISummaryStory>) =>
		stories?.length
			? stories.reduce(
					(accumulator, story) =>
						accumulator +
						`${commonTranslate('modelsInput.story')}: ${story.data}.` +
						(story?.summaryAcceptanceCriteria?.length
							? `${commonTranslate('modelsInput.scenarios')}: ` +
							  story.summaryAcceptanceCriteria.reduce(
									(criteriaAccumulator, criteria, index) =>
										criteriaAccumulator + `${index + 1}.${criteria.description}`,
									''
							  )
							: ''),
					''
			  )
			: '';

	const isPageDataFull = useMemo(
		() =>
			pageData.filter((item) => {
				if (item.tables[0].table) {
					const pageDataTable = JSON.parse(item.tables[0].table);

					return (
						!pageDataTable[UseCaseTableFields['Key Roles']]?.length &&
						!pageDataTable[UseCaseTableFields['Preconditions']]?.length &&
						!pageDataTable[UseCaseTableFields['Successful Outcome']]?.length &&
						!pageDataTable[UseCaseTableFields['Trigger']]?.length &&
						!pageDataTable[UseCaseTableFields['Main Scenario Flow of Events']]?.length
					);
				} else {
					return true;
				}
			}),
		[pageData]
	);

	const isUseCasesGeneratingFailed: boolean = useMemo(
		() =>
			Boolean(
				activeTicket?.messages.filter(
					(message) =>
						message.stage === TicketStages.useCases &&
						message.content === 'common.tokensErrorMessage'
				).length
			),
		[activeTicket?.messages]
	);

	const isRegenerateButtonActive = useMemo(
		() => activeStep === TicketStages.useCases && isUseCasesGeneratingFailed,
		[activeStep, isUseCasesGeneratingFailed]
	);

	const isSharedWithReadPermission = useMemo(() => {
		if (activeTicket?.permissions?.length) {
			const currentStagePermission = activeTicket.permissions.find(
				(permission) =>
					permission.ticketCurrentStep === TicketStages.useCases ||
					permission.ticketCurrentStep === null
			);

			if (currentStagePermission)
				return currentStagePermission.permission === SharePermissions.Read;
		}

		return false;
	}, [activeTicket?.id, activeTicket?.permissions]);

	useEffect(() => {
		addEventListener('messageSend', handleMessageSend);
		addEventListener('regenerateAccept', handleUseCasesRegenerate);
		addEventListener('errorRegenerateAccept', requestUseCases);

		return () => {
			removeEventListener('messageSend', handleMessageSend);
			removeEventListener('regenerateAccept', handleUseCasesRegenerate);
			removeEventListener('errorRegenerateAccept', requestUseCases);
		};
	}, [
		messages,
		activeTicket?.useCases,
		voiceMode,
		hasFinishedSpeaking,
		currentPageData,
		pageData,
		currentPage,
		isPageDataFull,
		choosedFile,
	]);

	const usecaseStageMessages = useMemo(() => {
		return messages.filter((message) => message.stage === TicketStages.useCases);
	}, [messages]);

	const getMessagesString = (messageList: Array<IMessage>): string => {
		return messageList
			.filter((value) => value.stage === TicketStages.notifications)
			.reduce((accumulator, value) => {
				return accumulator + `${value.sender}: ${value.content}\n`;
			}, '');
	};

	const getWelcomeMessage = () => ({
		content: 'useCasesStep.welcomeMessage',
		sender: MessageSender.System,
		sendTime: new Date().toISOString(),
		stage: TicketStages.useCases,
		action: {
			type: null,
			state: null,
		},
		userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
	});

	const getRegenerateMessage = (): IMessage => {
		const sendTime = new Date();
		sendTime.setTime(new Date().getTime() + 1);

		return {
			content: 'useCasesStep.regenerateMessage',
			sender: MessageSender.System,
			sendTime: sendTime.toISOString(),
			stage: TicketStages.useCases,
			action: {
				type: MessageActionTypes.regenerate,
				state: MessageActionStates.default,
			},
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};
	};

	const getEmptyDiagramsTemplate = (): Array<IDiagram> => [
		{ title: DiagramTypes['Use Case Diagram'], description: '', pictureLink: '' },
		{ title: DiagramTypes['Sequence Diagram'], description: '', pictureLink: '' },
		{ title: DiagramTypes['Flowchart'], description: '', pictureLink: '' },
	];

	useEffect(() => {
		const hasWelcomeMessage = usecaseStageMessages.filter(
			(message) => message?.action?.type === null
		).length;

		if (!hasWelcomeMessage && messages.length) {
			const welcomeMessage: IMessage = getWelcomeMessage();

			setMessages([...messages, welcomeMessage]);
		}
	}, [messages]);

	useEffect(() => {
		// Automatically generate UseCases when:
		// - pageData is empty (no UseCases displayed yet)
		// - activeTicket?.useCases is defined but empty (no UseCases in the ticket)
		// - Generation is not already in progress
		const shouldGenerateUseCases = 
			!pageData.length &&
			activeTicket?.useCases !== undefined &&
			activeTicket.useCases.length === 0 &&
			!isUseCasesGenerating;

		if (shouldGenerateUseCases) {
			requestUseCases();
		}
	}, [pageData.length, activeTicket?.useCases?.length, isUseCasesGenerating]);

	const handleMessageSend = (payload: any) => {
		const message: IMessage = { ...payload, stage: TicketStages.useCases };

		// Compter les caractères sans les retours à la ligne
		const contentWithoutNewLines = message?.content.replace(/(\r\n|\n|\r)/gm, '') || '';
		if (contentWithoutNewLines.length <= 4000) {
			const newMessagesList = messages.filter(
				(value) =>
					!(
						value?.action?.type === MessageActionTypes.regenerate &&
						value?.stage === TicketStages.useCases
					)
			);

			const updatedMessagesList = [...newMessagesList, message];
			setMessages(updatedMessagesList);

			dispatch(setIsTyping(true));
			const currentTicket = Object.assign({}, activeTicket);
			try {
				processMessageToChatGpt(message)
					.then((res) => {
						if (currentTicket.id !== activeTicket?.id) {
							setMessages([...newMessagesList, res]);
							dispatch(setIsTicketUpdate(true));
						} else {
							const newMessages = [...newMessagesList, message, res];

							if (voiceMode) playText(res.content);

							const sendTime = new Date();
							sendTime.setTime(new Date().getTime() + 1);

							const regenerateSummaryMessage: IMessage = getRegenerateMessage();

							newMessages.push(regenerateSummaryMessage);

							setMessages(newMessages);

							dispatch(setIsTicketUpdate(true));
						}
						dispatch(setIsTyping(false));
					})
					.catch((err) => {
						if (err.message === notEnoughTokensErrorMessage) {
							cleanUpChatOnError(updatedMessagesList);
						}
					});
			} catch (error) {
				console.log(error);
				dispatch(setIsTyping(false));
			}
		} else {
			errorNotify(commonTranslate('errors.error'), commonTranslate('errors.messageLengthError'));
		}
	};

	const getUsedMessages = () => {
		const messagesWithDisabledRegenerate = messages.map((message: IMessage) =>
			message?.action?.type === MessageActionTypes.regenerate
				? {
						...message,
						action: {
							...message.action,
							state: MessageActionStates.accept,
						},
				  }
				: message
		);

		return messagesWithDisabledRegenerate.map((message: IMessage) =>
			message.stage === TicketStages.useCases && message.sender === MessageSender.user
				? {
						...message,
						action: {
							...message.action,
							type: MessageActionTypes.used,
							state: MessageActionStates.default,
						},
				  }
				: message
		);
	};

	const processMessageToChatGpt = async (message: IMessage) => {
		return getUsecaseConversation({
			chatHistory: getMessagesString(messages),
			userValue: message.content,
			fileName: choosedFile?.name,
		})
			.then((response) => {
				const res: IMessage = {
					content: response,
					sender: MessageSender.chatGPT,
					sendTime: new Date().toISOString(),
					stage: TicketStages.useCases,
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				};
				return res;
			})
			.catch((err) => {
				if (err.message === notEnoughTokensErrorMessage)
					throw new Error(notEnoughTokensErrorMessage);

				return {
					content: '',
					sender: MessageSender.chatGPT,
					sendTime: new Date().toISOString(),
					stage: TicketStages.useCases,
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
				};
			});
	};

	const cleanUpChatOnError = (messagesList: Array<IMessage>) => {
		setMessages(messagesList.slice(0, -1));

		dispatch(setIsTyping(false));
	};

	let useCasesArray: Array<IUsecaseData> = [];

	const getDescriptionTablePromise = (useCase: IUseCase) =>
		new Promise((resolve) =>
			handleDescriptionTable(useCase).then((tableData) => {
				const currentUseCase = useCasesArray.find(
					(useCaseItem: IUsecaseData) => useCaseItem.title === useCase.title
				);

				const useCaseData = {
					title: useCase.title,
					description: useCase.description,
					relatedUserStories: useCase.relatedUserStories,
					diagrams: currentUseCase?.diagrams || getEmptyDiagramsTemplate(),
					tables: [
						{
							table: JSON.stringify(tableData),
						},
					],
				};

				if (currentUseCase) {
					useCasesArray = useCasesArray.map((useCaseItem) =>
						useCaseItem.title === useCase.title ? useCaseData : useCaseItem
					);
				} else {
					useCasesArray = [...useCasesArray, useCaseData];
				}

				dispatch(
					updateLocalTicket({
						id: activeTicket?.id,
						useCases: useCasesArray,
					})
				);

				dispatch(setIsUsecasesLoading(false));

				resolve(useCaseData);
			})
		);

	const getDiagramsPromise = (useCase: IUseCase, tableData: IParsedTableData) => {
		const diagramsArray = Object.values(DiagramTypes);

		useCasesArray = pageData;

		const resolveDiagramGenerate = (diagramData: any, index: number) => {
			const currentUseCase = useCasesArray.find(
				(useCaseItem) => useCaseItem.title === useCase.title
			);

			let diagrams: Array<IDiagram>;

			if (currentUseCase?.diagrams) {
				diagrams = currentUseCase.diagrams.map((diagram) =>
					diagram.title === diagramsArray[index] ? diagramData : diagram
				);
			} else {
				diagrams = getEmptyDiagramsTemplate().map((diagram) =>
					diagram.title === diagramsArray[index] ? diagramData : diagram
				);
			}

			const useCaseData = {
				title: useCase.title,
				description: useCase.description,
				relatedUserStories: useCase.relatedUserStories,
				diagrams: diagrams,
				tables: currentUseCase?.tables || [{ table: '{}' }],
			};

			if (currentUseCase) {
				useCasesArray = useCasesArray.map((useCaseItem) =>
					useCaseItem.title === useCase.title ? useCaseData : useCaseItem
				);
			} else {
				useCasesArray = [...useCasesArray, useCaseData];
			}

			dispatch(
				updateLocalTicket({
					id: activeTicket?.id,
					useCases: useCasesArray,
				})
			);
		};

		return Promise.all(
			diagramsArray.map(
				(diagramType, index) =>
					new Promise((resolve) => {
						generateUML(useCase, tableData, diagramType).then((diagramData: IRawDiagram) => {
							resolveDiagramGenerate(diagramData, index);

							resolve(diagramData);
						});
					})
			)
		);
	};

	const useCasesPromises = (useCase: IUseCase) =>
		new Promise((resolve) => {
			getDescriptionTablePromise(useCase).then(() => {
				resolve(true);
			});
		});

	const generateDiagrams = (page: number) => {
		setAreDiagramsLoading(true);

		const currentUseCaseData = pageData[page];

		const useCaseDescription: IUseCase = {
			title: currentUseCaseData?.title || '',
			description: currentUseCaseData?.description || '',
			relatedUserStories: currentUseCaseData?.relatedUserStories || [],
		};

		const useCaseTable = JSON.parse(currentUseCaseData.tables[0].table);

		getDiagramsPromise(useCaseDescription, useCaseTable).then((response: any) => {
			const updatedDiagrams = currentUseCaseData.diagrams.map((diagram) => ({
				...diagram,
				pictureLink: response.find(
					(responseDiagram: IRawDiagram) => responseDiagram.title === diagram.title
				).pictureLink,
			}));

			handleUpdateUseCases({ ...currentUseCaseData, diagrams: updatedDiagrams }).then(() =>
				setAreDiagramsLoading(false)
			);
		});
	};

	// load only two pages at a time to reduce load on the server.
	// once one page completes loading, load the next page (in case there are still pages left).
	const handleUseCases = async () => {
		let useCases: Array<IUseCase> = [];

		let currentPage = 0;
		let loadedPages = 0;

		const loadUseCasesWithLimit = (limit: number) => {
			const getPagePromise = (useCase: IUseCase, currentPageArg: number) =>
				new Promise((resolve) => {
					useCasesPromises(useCase)
						.then(() => {
							loadedPages += 1;

							if (useCases.length === 1) saveUsecases(useCasesArray);

							if (loadedPages === useCases.length + 1) {
								saveUsecases(useCasesArray);

								if (!isPageDataFull.length) {
									const messagesWithoutError = messages.filter(
										(message) => message.action?.type !== MessageActionTypes.error
									);

									setMessages(messagesWithoutError);
								}
							}
							if (currentPageArg <= useCases.length) {
								loadUseCasesWithLimit(limit);
							}
							resolve(true);
						})
						.then(() => {
							dispatch(fetchUserBalance());
						})
						.catch((e) => handleNotEnoughTokensError({ message: e.data }));
				});

			if (currentPage === 0) {
				currentPage += limit;

				for (const useCase of useCases.slice(0, limit)) {
					getPagePromise(useCase, currentPage);
				}
			}

			if (currentPage !== 0 && currentPage <= useCases.length) {
				currentPage += 1;

				getPagePromise(useCases[currentPage - 2], currentPage);
			}
		};

		if (pageData.length) {
			useCases = pageData.map((item) => ({
				title: item.title || '',
				description: item.description || '',
				relatedUserStories: item.relatedUserStories,
			}));

			loadUseCasesWithLimit(maxConcurrentUseCaseRequests);
		} else {
			getUserStoriesEpics({
				userStories: getIndexedFunctionalUserStories,
				fileName: choosedFile?.name,
			})
				.then((response) => {
					useCases = response.map((epic) => ({
						title: epic.title,
						description: epic.description,
						relatedUserStories: epic.relatedUserStoriesIndexes
							.filter(
								(index) => activeTicket?.ticketSummaries && activeTicket.ticketSummaries?.[index]
							)
							// @ts-ignore
							.map((index): ISummaryStory => activeTicket.ticketSummaries[index]),
					}));

					loadUseCasesWithLimit(maxConcurrentUseCaseRequests);
				})
				.catch((err) => {
					if (err.message === notEnoughTokensErrorMessage) handleTokensError(messages);

					dispatch(setIsUsecasesLoading(false));
				});
		}
	};

	const getCorrectedDiagramLink = async (code: string) => {
		const response = await fetch(getUmlUrl(code));

		if (response.status !== 200) {
			return await getDiagramValidation({ code, fileName: choosedFile?.name }).catch(() => code);
		}

		return code;
	};

	const generateUML = async (
		useCase: IUseCase,
		tableData: IParsedTableData,
		diagramType: keyof typeof DiagramTypes
	): Promise<IRawDiagram> => {
		let diagram;
		let isDiagramsError = false;

		const keyRoles = tableData['Key Roles'].reduce(
			(accumulator: string, item) =>
				accumulator +
				`${commonTranslate('modelsInput.role')} - ${item.Role}, ${commonTranslate(
					'modelsInput.responsibilities'
				)}: ${item.Responsibilities}`,
			''
		);

		// support for older tickets
		const parsedTableFlowOfEvents = tableData?.['Main Scenario Flow of Events'] || [];

		const mainScenario = parsedTableFlowOfEvents.reduce(
			(accumulator: string, item, index) => accumulator + `${index}).${item}`,
			''
		);

		if (diagramType === DiagramTypes['Use Case Diagram'])
			diagram = await getUseCaseDiagram({
				userStories: getRelatedUserStoriesWithScenarios(useCase.relatedUserStories),
				keyRoles,
				useCase: useCase.description,
				fileName: choosedFile?.name,
			}).catch(() => {
				isDiagramsError = true;
			});

		if (diagramType === DiagramTypes['Flowchart'])
			diagram = await getFlowDiagram({
				mainScenario,
				useCase: useCase.description,
				fileName: choosedFile?.name,
			}).catch(() => {
				isDiagramsError = true;
			});

		if (diagramType === DiagramTypes['Sequence Diagram'])
			diagram = await getSequenceDiagram({
				userStoriesWithScenarios: getRelatedUserStoriesWithScenarios(useCase.relatedUserStories),
				keyRoles,
				mainScenario,
				useCase: useCase.description,
				fileName: choosedFile?.name,
			}).catch(() => {
				isDiagramsError = true;
			});

		if (!isDiagramsError) {
			const correctedCode = await getCorrectedDiagramLink(diagram);

			return {
				title: diagramType,
				description: useCasesTranslate('diagrams.defaultDiagramDescription'),
				pictureLink: getUmlUrl(correctedCode),
				code: correctedCode,
			};
		} else {
			return {
				title: diagramType,
				description: useCasesTranslate('diagrams.defaultDiagramDescription'),
				pictureLink: '',
				code: '',
			};
		}
	};

	const regenerateDiagram = async (diagram: IDiagram) => {
		let updatedCode = '';
		const diagramType = diagram.title;

		const tableData: IParsedTableData = JSON.parse(currentPageData[0].tables[0].table);

		const keyRoles = tableData['Key Roles'].reduce(
			(accumulator: string, item) =>
				accumulator +
				`${commonTranslate('modelsInput.role')} - ${item.Role}, ${commonTranslate(
					'modelsInput.responsibilities'
				)}: ${item.Responsibilities}`,
			''
		);

		const parsedTableFlowOfEvents = tableData?.['Main Scenario Flow of Events'] || [];

		const mainScenario = parsedTableFlowOfEvents.reduce(
			(accumulator: string, item, index) => accumulator + `${index}).${item}`,
			''
		);

		if (diagramType === DiagramTypes['Use Case Diagram'])
			updatedCode = await getUseCaseDiagram({
				userStories: getRelatedUserStoriesWithScenarios(currentPageData[0].relatedUserStories),
				keyRoles,
				useCase: currentPageData[0]?.description || '',
				fileName: choosedFile?.name,
				regenerate: true,
			});

		if (diagramType === DiagramTypes['Flowchart'])
			updatedCode = await getFlowDiagram({
				mainScenario,
				useCase: currentPageData[0]?.description || '',
				fileName: choosedFile?.name,
				regenerate: true,
			});

		if (diagramType === DiagramTypes['Sequence Diagram'])
			updatedCode = await getSequenceDiagram({
				userStoriesWithScenarios: getRelatedUserStoriesWithScenarios(
					currentPageData[0].relatedUserStories
				),
				keyRoles,
				mainScenario,
				useCase: currentPageData[0]?.description || '',
				fileName: choosedFile?.name,
				regenerate: true,
			});

		const updatedDiagram = { ...diagram, code: updatedCode, pictureLink: getUmlUrl(updatedCode) };
		const updatedDiagrams = currentPageData[0]?.diagrams.map((diagramItem) =>
			diagramItem.title === diagramType ? updatedDiagram : diagramItem
		);

		await handleUpdateUseCases({ ...currentPageData[0], diagrams: updatedDiagrams });

		return;
	};

	const getKeyRolesPromise = ({ userStories, useCase }: ITableDataProps) =>
		getKeyRoles({ userStories, useCase, fileName: choosedFile?.name }).catch((err) => {
			if (err.message === notEnoughTokensErrorMessage) throw new Error(notEnoughTokensErrorMessage);
		});

	const getPreconditionsPromise = ({ userStories, useCase }: ITableDataProps) =>
		getPreconditions({ userStories, useCase, fileName: choosedFile?.name }).catch((err) => {
			if (err.message === notEnoughTokensErrorMessage) throw new Error(notEnoughTokensErrorMessage);
		});

	const getTriggerPromise = ({ userStories, useCase }: ITableDataProps) =>
		getTrigger({ userStories, useCase, fileName: choosedFile?.name }).catch((err) => {
			if (err.message === notEnoughTokensErrorMessage) throw new Error(notEnoughTokensErrorMessage);
		});

	const getSuccessfulOutcomePromise = ({ userStories, useCase }: ITableDataProps) =>
		getSuccessfulOutcome({
			userStoriesAcceptanceCriteria: userStories,
			useCase,
			fileName: choosedFile?.name,
		}).catch((err) => {
			if (err.message === notEnoughTokensErrorMessage) throw new Error(notEnoughTokensErrorMessage);
		});

	const getFlowOfEventsPromise = ({ userStories, useCase }: ITableDataProps) =>
		getFlowOfEvents({
			userStoriesWithScenarios: userStories,
			useCase,
			fileName: choosedFile?.name,
		}).catch((err) => {
			if (err.message === notEnoughTokensErrorMessage) throw new Error(notEnoughTokensErrorMessage);
		});

	const handleDescriptionTable = async (usecase: IUseCase) => {
		let dataToFetchArray: Array<any>;

		const currentUsecaseData = pageData.find((item) => item.title === usecase.title);

		if (currentUsecaseData) {
			const currentTableData = JSON.parse(currentUsecaseData.tables[0].table);

			const keyRoles = currentTableData[UseCaseTableFields['Key Roles']]?.length
				? currentTableData[UseCaseTableFields['Key Roles']]
				: getKeyRolesPromise({
						userStories: getRelatedUserStories(usecase.relatedUserStories),
						useCase: usecase,
				  });

			const preconditions = currentTableData[UseCaseTableFields['Preconditions']]?.length
				? currentTableData[UseCaseTableFields['Preconditions']]
				: getPreconditionsPromise({
						userStories: getRelatedUserStories(usecase.relatedUserStories),
						useCase: usecase,
				  });

			const trigger = currentTableData[UseCaseTableFields['Trigger']]?.length
				? currentTableData[UseCaseTableFields['Trigger']]
				: getTriggerPromise({
						userStories: getRelatedUserStories(usecase.relatedUserStories),
						useCase: usecase,
				  });

			const successfulOutcome = currentTableData[UseCaseTableFields['Successful Outcome']]?.length
				? currentTableData[UseCaseTableFields['Successful Outcome']]
				: getSuccessfulOutcomePromise({
						userStories: getRelatedUserStoriesWithAcceptanceCriteria(usecase.relatedUserStories),
						useCase: usecase,
				  });

			const flowOfEvents = currentTableData[UseCaseTableFields['Main Scenario Flow of Events']]
				?.length
				? currentTableData[UseCaseTableFields['Main Scenario Flow of Events']]
				: getFlowOfEventsPromise({
						userStories: getRelatedUserStoriesWithScenarios(usecase.relatedUserStories),
						useCase: usecase,
				  });

			dataToFetchArray = [keyRoles, preconditions, trigger, successfulOutcome, flowOfEvents];
		} else {
			dataToFetchArray = [
				getKeyRolesPromise({
					userStories: getRelatedUserStories(usecase.relatedUserStories),
					useCase: usecase,
				}),
				getPreconditionsPromise({
					userStories: getRelatedUserStories(usecase.relatedUserStories),
					useCase: usecase,
				}),
				getTriggerPromise({
					userStories: getRelatedUserStories(usecase.relatedUserStories),
					useCase: usecase,
				}),
				getSuccessfulOutcomePromise({
					userStories: getRelatedUserStoriesWithAcceptanceCriteria(usecase.relatedUserStories),
					useCase: usecase,
				}),
				getFlowOfEventsPromise({
					userStories: getRelatedUserStoriesWithScenarios(usecase.relatedUserStories),
					useCase: usecase,
				}),
			];
		}

		return Promise.allSettled(dataToFetchArray).then((response) => {
			const requestHasErrors = response.filter((item) => item.status === 'rejected');

			if (requestHasErrors.length) handleTokensError(messages);

			return {
				'Key Roles':
					response[0].status === 'fulfilled'
						? response[0].value
						: { Role: '', Responsibilities: '' },
				Preconditions: response[1].status === 'fulfilled' ? response[1].value : [],
				Trigger: response[2].status === 'fulfilled' ? response[2].value : [],
				'Successful Outcome': response[3].status === 'fulfilled' ? response[3].value : [],
				'Main Scenario Flow of Events': response[4].status === 'fulfilled' ? response[4].value : [],
			};
		});
	};

	const updateDiagrams = async (changeRequests: Array<IRequestDetails>, type: string) => {
		const diagramsChanges = changeRequests.filter((request) => request.type === type);

		if (diagramsChanges.length) {
			const changeRequest = diagramsChanges.reduce((accumulator, value, index) => {
				return accumulator + `${index + 1}. ${value.request}\n`;
			}, '');

			const targetDiagram = currentPageData[0].diagrams.find((diagram) => diagram.title === type);

			if (targetDiagram) {
				let diagramCorrectionError = false;

				const diagramCode =
					targetDiagram?.code ||
					plantumlEncoder.decode(targetDiagram.pictureLink.split('/').pop() || '');

				const updatedDiagramCode = await getDiagramCorrection({
					diagramCode,
					diagramType: type,
					requirements: changeRequest,
					fileName: choosedFile?.name,
				}).catch(() => {
					diagramCorrectionError = true;
				});

				if (!diagramCorrectionError) {
					const correctedDiagramCode = await getCorrectedDiagramLink(updatedDiagramCode);

					const updatedDiagram: IDiagram = {
						...targetDiagram,
						code: updatedDiagramCode,
						pictureLink: getUmlUrl(correctedDiagramCode),
					};

					return updatedDiagram;
				} else {
					throw new Error(notEnoughTokensErrorMessage);
				}
			}
		}

		return false;
	};

	const handleUseCasesRegenerate = async () => {
		dispatch(setIsUsecasesLoading(true));

		const newRequests = messages.filter(
			(message: IMessage) =>
				message.sender === MessageSender.user &&
				message.stage === TicketStages.useCases &&
				message.action?.type !== MessageActionTypes.used
		);

		const formattedRequests = newRequests.map((request) =>
			getDiagramChangeRequest({
				changeRequest: request.content,
				fileName: choosedFile?.name,
			}).catch((err) => {
				if (err.message === notEnoughTokensErrorMessage)
					throw new Error(notEnoughTokensErrorMessage);
			})
		);

		Promise.all(formattedRequests)
			.then((changeRequests) => {
				let currentPageCopy = { ...currentPageData[0] };

				const updateDiagramPromises = getNumericEnumValuesArray(DiagramTypes).map((requestType) => {
					return new Promise((resolve) => {
						updateDiagrams(changeRequests, requestType)
							.then((updatedDiagram) => {
								if (updatedDiagram) {
									currentPageCopy = {
										...currentPageCopy,
										diagrams: currentPageCopy.diagrams.map((diagram) =>
											diagram.title === requestType ? updatedDiagram : diagram
										),
									};
								}

								resolve(true);
							})
							.catch(() => {
								resolve(false);
							});
					});
				});

				Promise.all(updateDiagramPromises).then((res) => {
					const isDiagramsChangeFailed = res.filter((value) => !value);

					if (!isDiagramsChangeFailed.length) {
						const updatedPageData = pageData.map((page, index) =>
							index === currentPage - 1 ? currentPageCopy : page
						);

						setMessages(getUsedMessages());

						saveUsecases(updatedPageData);

						dispatch(setIsTicketUpdate(true));
					}

					dispatch(setIsUsecasesLoading(false));
				});
			})
			.catch(() => {
				dispatch(setIsUsecasesLoading(false));
			});
	};

	const requestUseCases = async () => {
		dispatch(setIsUsecasesLoading(true));
		dispatch(setIsUsecasesGenerating(true));

		await handleUseCases();

		dispatch(setIsTicketUpdate(true));
	};

	const handleCloseModal = () => {
		setIsFullViewDiagramModalOpen(false);
	};

	const saveUsecases = (usecases: Array<IUsecaseData>) => {
		const useCasesData: IUsecases[] = [];

		if (activeTicket) {
			usecases?.forEach((data, index) => {
				const useCaseData = {
					usecase: {
						title: data.title,
						description: data.description,
						tables: data.tables,
						diagrams: data.diagrams.map((diagram) => ({
							title: diagram.title,
							description: diagram.description,
							pictureLink: diagram.pictureLink,
						})),
					},
					order: index,
					ticketId: activeTicket.id,
					ticketSummaryIds: data.relatedUserStories.map(({ id }) => id) as string[],
				};

				useCasesData.push(useCaseData);
			});
		}

		if (useCasesData.length && activeTicket?.id) {
			dispatch(deleteTicketUsecases({ ticketId: activeTicket.id })).then(() => {
				dispatch(saveUseCases({ useCasesData }))
					.unwrap()
					.then((response) => {
						dispatch(
							updateLocalTicket({
								id: activeTicket?.id,
								useCases: response,
							})
						);
						dispatch(setIsUsecasesGenerating(false));
					});
			});
		}
	};

	const handleUpdateUseCases = (useCaseData: IUsecaseData) =>
		new Promise((resolve) => {
			if (useCaseData?.id) {
				const formattedUseCaseData = {
					...useCaseData,
					diagrams: useCaseData.diagrams.map((diagram) => ({
						id: diagram.id,
						title: diagram.title,
						description: diagram.description,
						pictureLink: diagram.pictureLink,
					})),
				};

				const updatedUseCases = pageData.map((page, index) =>
					index + 1 === currentPage ? useCaseData : page
				);

				dispatch(
					updateUseCases({ useCaseData: formattedUseCaseData, useCaseId: useCaseData.id })
				).then(() => {
					dispatch(
						updateLocalTicket({
							id: activeTicket?.id,
							useCases: updatedUseCases,
						})
					);

					resolve(true);
				});
			}
		});

	const handleTokensError = (messageList: Array<IMessage>) => {
		const acceptedGenerateMessage = messageList.filter(
			(message) => message.action?.type !== MessageActionTypes.error
		);

		const welcomeMessage = getWelcomeMessage();

		const sendTime = new Date();
		sendTime.setTime(new Date().getTime() + 1);

		const errorRegenerateMessage: IMessage = {
			content: 'common.tokensErrorMessage',
			sender: MessageSender.System,
			sendTime: sendTime.toISOString(),
			stage: TicketStages.useCases,
			action: {
				type: MessageActionTypes.error,
				state: MessageActionStates.default,
			},
			userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
		};

		const hasWelcomeMessage = usecaseStageMessages.filter(
			(message) => message?.content === 'useCasesStep.welcomeMessage'
		).length;

		if (!hasWelcomeMessage && messageList.length) {
			setMessages([...acceptedGenerateMessage, welcomeMessage, errorRegenerateMessage]);
		} else {
			setMessages([...acceptedGenerateMessage, errorRegenerateMessage]);
		}

		dispatch(triggerTicketSave());
	};

	const showFullViewDiagram = (diagram: IDiagram) => {
		setIsFullViewDiagramModalOpen(true);
		setModalDiagramObj(diagram);
	};

	const handleNextStepNavigation = () => {
		dispatch(setActiveStep(TicketStages.testCases));

		if (TicketStages.notifications > (activeTicket?.currentStep || 0))
			dispatch(triggerTicketSave());
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', justifyContent: 'center' }}>
			{activeTicket && (
				<Box
					data-testid={'useCases-step'}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
						position: 'relative',
						height: '100%',
						overflow: 'auto',
					}}
				>
					{editMode ? (
						<>
							<EditUsecasesStep
								allPagesData={pageData}
								pageData={currentPageData[0]}
								setPageData={setPageData}
								pageIndex={currentPage}
								ticketTitle={ticketTitle}
								setTitleError={setTitleError}
							/>
						</>
					) : (
						<Box
							className="rtl-able"
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: isRequestLoading ? 'center' : 'space-between',
								width: '100%',
								height: '100%',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								<Box
									sx={{
										height: '100%',
										overflow: 'auto',
										padding: i18n?.language === 'ar' ? '0 12px 8px 16px' : '0 16px 8px 0',
									}}
									ref={usecaseRef}
								>
									{isRequestLoading ? (
										<Box
											sx={{
												width: '100%',
												height: '100%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												margin: 'auto',
											}}
										>
											<Oval
												height={24}
												width={24}
												color="#fff"
												wrapperStyle={{}}
												wrapperClass=""
												visible={true}
												ariaLabel="oval-loading"
												secondaryColor="#4786ff"
												strokeWidth={5}
												strokeWidthSecondary={5}
											/>
										</Box>
									) : (
										<>
											{currentPageData.map((data, index) => (
												<Box key={index}>
													<Typography
														sx={{
															fontWeight: 700,
															marginBottom: { md: '8px', xl: '20px' },
															color: titleTextColor,
														}}
													>
														{useCasesTranslate('generalText.useCase')}: {data?.title}
													</Typography>

													<Typography
														sx={{
															marginBottom: { md: '12px', xl: '20px' },
															color: regularTextColor,
														}}
													>
														{data.description ||
															useCasesTranslate('descriptionTable.introductionText')}
													</Typography>

													<Typography
														sx={{
															fontWeight: 700,
															color: titleTextColor,
														}}
													>
														{useCasesTranslate('generalText.userStories')}
													</Typography>

													<UserStories>
														<UserStoriesList ticketSummaries={data.relatedUserStories} />
													</UserStories>

													<Box
														sx={{
															display: 'flex',
															width: '100%',
															justifyContent: 'flex-start',
															alignItems: 'center',
															position: 'relative',
															minHeight: '300px',
														}}
													>
														{Object.keys(JSON.parse(data.tables?.[0]?.table)).length ? (
															<Box data-testid={'description-table-block'}>
																{generateUseCaseDescriptionJSX(
																	JSON.parse(data.tables?.[0]?.table),
																	undefined,
																	undefined,
																	undefined,
																	translationConfig
																)}
															</Box>
														) : (
															<Oval
																height={24}
																width={24}
																color="#fff"
																wrapperStyle={{}}
																wrapperClass=""
																visible={true}
																ariaLabel="oval-loading"
																secondaryColor="#4786ff"
																strokeWidth={5}
																strokeWidthSecondary={5}
															/>
														)}
													</Box>

													{data.diagrams.filter((diagram: IDiagram) => diagram.pictureLink)
														.length ? (
														orderDiagrams(data.diagrams).map((diagram, index) => (
															<Box key={index} data-testid={'diagram-block'}>
																<Typography
																	sx={{
																		fontWeight: 700,
																		marginTop: { md: '8px', xl: '16px' },
																		color: titleTextColor,
																	}}
																>
																	{useCasesTranslate(`diagrams.${diagram.title}`)}
																</Typography>
																<Typography
																	sx={{
																		marginBottom: { md: '8px', xl: '20px' },
																		color: regularTextColor,
																	}}
																>
																	{useCasesTranslate('diagrams.defaultDiagramDescription')}
																</Typography>
																<Box
																	sx={{
																		display: 'flex',
																		justifyContent: 'center',
																		alignItems: 'center',
																		position: 'relative',
																		minHeight: '300px',
																	}}
																>
																	{diagram?.pictureLink ? (
																		<DiagramImage
																			diagram={diagram}
																			onFullView={showFullViewDiagram}
																			onLoadError={regenerateDiagram}
																		/>
																	) : (
																		<Oval
																			height={24}
																			width={24}
																			color="#fff"
																			wrapperStyle={{}}
																			wrapperClass=""
																			visible={true}
																			ariaLabel="oval-loading"
																			secondaryColor="#4786ff"
																			strokeWidth={5}
																			strokeWidthSecondary={5}
																		/>
																	)}
																</Box>
															</Box>
														))
													) : areDiagramsLoading ? (
														<Box
															sx={{
																width: '100%',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															<Oval
																height={24}
																width={24}
																color="#fff"
																wrapperStyle={{}}
																wrapperClass=""
																visible={true}
																ariaLabel="oval-loading"
																secondaryColor="#4786ff"
																strokeWidth={5}
																strokeWidthSecondary={5}
															/>
														</Box>
													) : null}

													{isRegenerateButtonActive ? (
														<Button
															className="not-rtl-able"
															sx={{
																height: '44px',
																width: '100%',
																display: 'flex',
																gap: '8px',
																textTransform: 'none',
																fontWeight: '600',
																marginTop: '16px',
															}}
															variant="contained"
															color="primary"
															onClick={requestUseCases}
														>
															{useCasesTranslate('buttons.regenerateUseCases')}
														</Button>
													) : null}
												</Box>
											))}
										</>
									)}
								</Box>
							</Box>

							{isFullViewDiagramModalOpen && modalDiagramObj && (
								<FullViewDiagramModal
									handleCloseModal={handleCloseModal}
									diagram={modalDiagramObj}
								/>
							)}
						</Box>
					)}
				</Box>
			)}

			{pageData.length > 0 && !isRequestLoading && !areDiagramsLoading && !editMode ? (
				<>
					<Divider sx={{ background: '#E7E8EC', margin: '16px 0' }} />

					<Box
						className="not-rtl-able"
						sx={{
							width: '100%',
							display: 'flex',
							justifyContent: 'space-between',
							gap: { xs: '16px', md: '12px', xl: '16px' },
						}}
					>
						{!isPageDataFull.length &&
						!currentPageData[0].diagrams.filter((diagram: IDiagram) => diagram.pictureLink)
							.length &&
						!areDiagramsLoading ? (
							<Box>
								<Button
									sx={{
										height: '44px',
										display: 'flex',
										gap: '8px',
										padding: '12px 16px',
										textTransform: 'none',
										fontWeight: '600',
									}}
									variant="outlined"
									color="primary"
									disabled={isUseCasesGenerating || isSharedWithReadPermission}
									onClick={() => generateDiagrams(currentPage - 1)}
								>
									<RegenerateIcon />
									{useCasesTranslate('buttons.generateDiagrams')}
								</Button>
							</Box>
						) : null}

						<Stack spacing={2}>
							<Pagination
								data-testid={'use-case-pagination'}
								count={Math.ceil(pageData.length / linksPerPage)}
								page={currentPage}
								onChange={handlePageChange}
								variant="outlined"
								shape="rounded"
								disabled={areDiagramsLoading}
							/>
						</Stack>
						{!isPageDataFull.length && isNavigationButtonAvailable ? (
							<Button
								sx={{
									height: '44px',
									display: 'flex',
									gap: '8px',
									padding: '12px 16px',
									textTransform: 'none',
									fontWeight: '600',
								}}
								variant="contained"
								color="primary"
								disabled={isUseCasesGenerating}
								onClick={handleNextStepNavigation}
							>
								{activeTicket?.userStoryTests?.length
									? useCasesTranslate('buttons.goToTestCases')
									: useCasesTranslate('buttons.generateTestCases')}
								<Stars style={{ marginLeft: '4px', width: '20px' }} />
							</Button>
						) : null}
					</Box>
				</>
			) : null}
		</Box>
	);
};
